/**
 * CHROME TAG REVIEW - Cleanup utility
 */

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, getDocs, doc, updateDoc } from 'firebase/firestore';
import { onAuthStateChanged } from 'firebase/auth';
import { db, auth } from '../firebase/config';
import { ArrowLeft, Check, X, Loader2, SkipForward, Tag, AlertCircle } from 'lucide-react';
import type { CollectionItem } from '../types';

const FINISH_TO_REVIEW = 'chrome';

export default function ReviewChromeTags() {
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState<CollectionItem[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [saving, setSaving] = useState(false);
  const [stats, setStats] = useState({ reviewed: 0, kept: 0, removed: 0 });
  const [userId, setUserId] = useState<string | null>(null);

  // Wait for auth state
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user) {
        setUserId(user.uid);
      } else {
        setUserId(null);
        setLoading(false);
      }
    });
    return () => unsubscribe();
  }, []);

  // Load items once we have a userId
  useEffect(() => {
    if (!userId) return;

    const loadItems = async () => {
      try {
        const itemsRef = collection(db, 'users', userId, 'items');
        const snapshot = await getDocs(itemsRef);

        const chromeItems: CollectionItem[] = [];
        let totalItems = 0;
        let polishCount = 0;
        let withFinishArray = 0;

        snapshot.forEach((docSnap) => {
          const data = docSnap.data();
          totalItems++;

          if (data.itemType !== 'polish') return;
          polishCount++;

          const finish = data.finish || [];
          if (finish.length > 0) withFinishArray++;

          if (finish.some((f: string) => f.toLowerCase() === FINISH_TO_REVIEW.toLowerCase())) {
            chromeItems.push({ id: docSnap.id, ...data } as CollectionItem);
          }
        });

        chromeItems.sort((a, b) => {
          const aConflict = hasConflictingColorShift(a);
          const bConflict = hasConflictingColorShift(b);
          if (aConflict && !bConflict) return -1;
          if (!aConflict && bConflict) return 1;
          return 0;
        });

        setItems(chromeItems);
      } catch (err) {
        console.error('Error loading items:', err);
      } finally {
        setLoading(false);
      }
    };

    loadItems();
  }, [userId]);

  const hasConflictingColorShift = (item: CollectionItem): boolean => {
    const colorShift = (item as any).colorShift;
    if (!colorShift) return false;

    // Handle both string and array formats
    if (typeof colorShift === 'string') {
      const lower = colorShift.toLowerCase();
      return lower === 'duochrome' || lower === 'multichrome';
    }

    if (Array.isArray(colorShift)) {
      const lowerShifts = colorShift.map((c: string) => c.toLowerCase());
      return lowerShifts.includes('duochrome') || lowerShifts.includes('multichrome');
    }

    return false;
  };

  const getPhotoUrl = (item: CollectionItem): string | null => {
    if (item.photos && item.photos.length > 0) {
      const photo = item.photos[0];
      return typeof photo === 'string' ? photo : photo.url;
    }
    return item.photoUrl || null;
  };

  const handleKeep = () => {
    setStats(prev => ({ ...prev, reviewed: prev.reviewed + 1, kept: prev.kept + 1 }));
    setCurrentIndex(prev => prev + 1);
  };

  const handleRemove = async () => {
    if (!userId) return;

    const item = items[currentIndex];
    if (!item) return;

    setSaving(true);

    try {
      const currentFinish = (item as any).finish || [];
      const newFinish = currentFinish.filter(
      (f: string) => f.toLowerCase() !== FINISH_TO_REVIEW.toLowerCase()
      );

      const itemRef = doc(db, 'users', userId, 'items', item.id);
      await updateDoc(itemRef, { finish: newFinish });

      setStats(prev => ({ ...prev, reviewed: prev.reviewed + 1, removed: prev.removed + 1 }));
      setCurrentIndex(prev => prev + 1);
    } catch (err) {
      console.error('Error removing finish:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleSkip = () => {
    setCurrentIndex(prev => prev + 1);
  };

  const currentItem = items[currentIndex];
  const isComplete = currentIndex >= items.length;
  const hasConflict = currentItem ? hasConflictingColorShift(currentItem) : false;

  if (loading) {
    return (
    <div className="min-h-screen py-8 px-4 flex items-center justify-center">
      <div className="text-center">
        <Loader2 className="w-12 h-12 animate-spin text-glass mx-auto mb-4" />
        <p className="text-glass-secondary">Finding items with "{FINISH_TO_REVIEW}" finish...</p>
      </div>
    </div>
    );
  }

  return (
  <div className="min-h-screen py-8 px-4">
    <div className="max-w-lg mx-auto">
      <button onClick={() => navigate(-1)} className="mb-4 flex items-center gap-2 text-on-bg transition-default font-body">
        <ArrowLeft className="w-5 h-5" /><span>Back</span>
      </button>

      <div className="glass-card p-6 mb-6 text-center">
        <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-violet-500 mb-4">
          <Tag className="w-8 h-8 text-white" />
        </div>
        <h1 className="text-2xl font-bold text-glass mb-2">Review Chrome Finish</h1>
        <p className="text-glass-secondary text-sm">{items.length} items with "{FINISH_TO_REVIEW}" finish</p>
      </div>

      {items.length > 0 && (
      <div className="glass-card p-4 mb-6">
        <div className="flex justify-between text-sm text-glass-secondary mb-2">
          <span>Progress</span>
          <span>{Math.min(currentIndex + 1, items.length)} / {items.length}</span>
        </div>
        <div className="w-full bg-slate-700 rounded-full h-2 mb-3">
          <div className="bg-violet-500 h-2 rounded-full transition-all" style={{ width: `${(currentIndex / items.length) * 100}%` }} />
        </div>
        <div className="flex justify-center gap-6 text-sm">
          <span className="text-green-400">✓ Kept: {stats.kept}</span>
          <span className="text-red-400">✗ Removed: {stats.removed}</span>
        </div>
      </div>
      )}

      {items.length === 0 && (
      <div className="glass-card p-8 text-center">
        <div className="text-6xl mb-4">✨</div>
        <h2 className="text-xl font-bold text-glass mb-2">No Items Found</h2>
        <p className="text-glass-secondary">No items have the "{FINISH_TO_REVIEW}" finish.</p>
        <button onClick={() => navigate('/collection')} className="mt-6 px-6 py-3 bg-indigo-600 text-white rounded-xl font-semibold hover:bg-indigo-700 transition-default">
          Back to Collection
        </button>
      </div>
      )}

      {items.length > 0 && isComplete && (
      <div className="glass-card p-8 text-center">
        <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-green-500 mb-4">
          <Check className="w-8 h-8 text-white" />
        </div>
        <h2 className="text-2xl font-bold text-glass mb-2">All Done!</h2>
        <p className="text-glass-secondary mb-6">Reviewed {stats.reviewed} items. Kept {stats.kept}, removed "{FINISH_TO_REVIEW}" from {stats.removed}.</p>
        <button onClick={() => navigate('/collection')} className="px-6 py-3 bg-indigo-600 text-white rounded-xl font-semibold hover:bg-indigo-700 transition-default">
          Back to Collection
        </button>
      </div>
      )}

      {currentItem && !isComplete && (
      <div className="glass-card overflow-hidden">
        {hasConflict && (
        <div className="bg-amber-500/20 px-4 py-2 flex items-center gap-2 text-amber-300 text-sm">
          <AlertCircle className="w-4 h-4" />
          <span>Also tagged: duochrome or multichrome</span>
        </div>
        )}

        <div className="aspect-square bg-slate-800">
          {getPhotoUrl(currentItem) ? (
          <img src={getPhotoUrl(currentItem)!} alt={currentItem.name} className="w-full h-full object-cover" />
          ) : (
          <div className="w-full h-full flex items-center justify-center text-6xl">💅</div>
          )}
        </div>

        <div className="p-4">
          <p className="text-glass-muted text-sm">{currentItem.brand}</p>
          <h3 className="text-xl font-bold text-glass mb-3">{currentItem.name}</h3>

          <div className={((currentItem as any).colorShift || []).length > 0 ? "mb-2" : "mb-4"}>
            <span className="text-xs text-glass-muted">Finish:</span>
            <div className="flex flex-wrap gap-1.5 mt-1">
              {((currentItem as any).finish || []).map((finish: string, i: number) => (
              <span key={i} className={`px-2 py-0.5 rounded-full text-xs font-medium ${finish.toLowerCase() === FINISH_TO_REVIEW.toLowerCase() ? 'bg-violet-500 text-white' : 'bg-slate-600 text-slate-300'}`}>
                      {finish}
                    </span>
              ))}
            </div>
          </div>

          {(currentItem as any).colorShift && (
          <div className="mb-4">
            <span className="text-xs text-glass-muted">Color Shift:</span>
            <div className="flex flex-wrap gap-1.5 mt-1">
              {(Array.isArray((currentItem as any).colorShift)
              ? (currentItem as any).colorShift
              : [(currentItem as any).colorShift]
              ).map((shift: string, i: number) => (
              <span key={i} className={`px-2 py-0.5 rounded-full text-xs font-medium ${shift.toLowerCase() === 'duochrome' || shift.toLowerCase() === 'multichrome' ? 'bg-amber-500/30 text-amber-300' : 'bg-slate-600 text-slate-300'}`}>
          {shift}
        </span>
              ))}
            </div>
          </div>
          )}

          <div className="flex gap-2">
            <button onClick={handleRemove} disabled={saving} className="flex-1 px-4 py-3 bg-red-500 text-white rounded-xl font-semibold hover:bg-red-600 transition-default flex items-center justify-center gap-2 disabled:opacity-50">
              {saving ? <Loader2 className="w-5 h-5 animate-spin" /> : <><X className="w-5 h-5" />Remove Chrome</>}
            </button>
            <button onClick={handleKeep} disabled={saving} className="flex-1 px-4 py-3 bg-green-500 text-white rounded-xl font-semibold hover:bg-green-600 transition-default flex items-center justify-center gap-2 disabled:opacity-50">
              <Check className="w-5 h-5" />Keep
            </button>
          </div>

          <button onClick={handleSkip} disabled={saving} className="w-full mt-2 px-4 py-2 text-glass-muted hover:text-glass transition-default flex items-center justify-center gap-1 text-sm">
            <SkipForward className="w-4 h-4" />Skip for now
          </button>
        </div>
      </div>
      )}
    </div>
  </div>
  );
}